import axios from 'axios';
import type { DataResponse, PaginationParams } from './types';
import { requestWithPagination, request } from '@/utils/request';
// 类型定义
export interface LoginParams {
  telephone: string;
  password: string;
  verification_code?: string;
}

export interface SignUpParams {
  telephone: string;
  password: string;
  verification_code: string;
  nick_name: string;
  real_name: string;
  role_id?: number;
  company_id?: number;
}

// 这些类型保留，因为它们是业务实体类型
export interface PassportInfo {
  id: number;
  telephone: string;
  nick_name: string;
  real_name: string;
  role_id: number;
  company_id: number;
  status: number;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: number;
  status: number;
  company_id: number;
  company_name: string;
}

export interface UserQuery extends PaginationParams {
  username?: string;
  company_id?: number;
  status?: number;
}

export interface UserCreate {
  username: string;
  email: string;
  password: string;
  full_name: string;
  role: number;
  company_id: number;
}

export interface UserUpdate {
  email?: string;
  full_name?: string;
  role?: number;
  status?: number;
  password?: string;
}

export interface LoginData {
  telephone: string;
  password: string;
  verification_code?: string;
}

// API 函数
export async function login(data: LoginParams, captchaKey: string): Promise<DataResponse<{ access_token: string; token_type: string }>> {
  const response = await axios.post<DataResponse<{ access_token: string; token_type: string }>>(
    `/v1/passport/sign-in`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Captcha-Key': captchaKey
      }
    }
  );
  return response.data;
}

export async function logout(): Promise<void> {
  return axios.post(`/v1/passport/sign-out`);
}

export async function getUserInfo(): Promise<PassportInfo> {
  const response = await axios.get<DataResponse<PassportInfo>>(`/v1/passport/me`);
  return response.data.data;
}

export async function getPassportList(params: PaginationParams & {
  status?: number;
  role_id?: number;
  company_id?: number;
  }) {
  return requestWithPagination(axios.get('/v1/passport/list', { params }));
}

export async function createPassport(data: SignUpParams): Promise<PassportInfo> {
  const response = await axios.post<DataResponse<PassportInfo>>(
    `/v1/passport/sign-up`,
    data
  );
  return response.data.data;
}

export async function updatePassport(
  id: number,
  data: Partial<SignUpParams>
): Promise<PassportInfo> {
  const response = await axios.put<DataResponse<PassportInfo>>(
    `/v1/passport/${id}`,
    data
  );
  return response.data.data;
}

export async function deletePassport(id: number): Promise<void> {
  return axios.delete(`/v1/passport/${id}`);
}

export async function sendVerificationCode(telephone: string): Promise<void> {
  return axios.post(`/v1/passport/send-verification-code`, { telephone });
}

/**
 * 获取用户列表
 */
export const getUserList = async (params: UserQuery): Promise<DataResponse<User[]>> => {
  return axios.get<DataResponse<User[]>>('/v1/users/', { params }).then(res => res.data);
};

/**
 * 获取单个用户详情
 */
export function getUser(id: number): Promise<DataResponse<User>> {
  return axios.get<DataResponse<User>>(`/v1/users/${id}`).then(res => res.data);
}

/**
 * 创建新用户
 */
export function createUser(user: UserCreate): Promise<DataResponse<User>> {
  return axios.post<DataResponse<User>>('/v1/users/', user).then(res => res.data);
}

/**
 * 更新用户信息
 */
export function updateUser(id: number, user: UserUpdate): Promise<DataResponse<User>> {
  return axios.put<DataResponse<User>>(`/v1/users/${id}`, user).then(res => res.data);
}

/**
 * 删除用户
 */
export function deleteUser(id: number): Promise<DataResponse<null>> {
  return axios.delete<DataResponse<null>>(`/v1/users/${id}`).then(res => res.data);
}

/**
 * 修改密码
 */
export function changePassword(id: number, oldPassword: string, newPassword: string): Promise<DataResponse<null>> {
  return axios.post<DataResponse<null>>(`/v1/users/${id}/change-password`, {
    old_password: oldPassword,
    new_password: newPassword
  }).then(res => res.data);
}

/**
 * 重置密码
 */
export function resetPassword(id: number): Promise<DataResponse<{ password: string }>> {
  return axios.post<DataResponse<{ password: string }>>(`/v1/users/${id}/reset-password`, {}).then(res => res.data);
}

export function userLogin(
  telephone: string,
  password: string,
  verification_code: string,
  config?: any
): Promise<DataResponse<{ access_token: string; token_type: string }>> {
  return axios.post<DataResponse<{ access_token: string; token_type: string }>>(
    '/v1/passport/sign-in', 
    {
      telephone,
      password,
      verification_code,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(config?.headers || {})
      }
    }
  ).then(res => res.data);
}
